import React, { useState } from 'react';
import '../styles/AdminPanel.css';
import { compressImage } from '../utils/imageCompression';

const AdminPanel = ({ currentLang, potteryItems, onLogout, onAdd, onUpdate, onDelete }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({
        titleEn: '',
        titleAr: '',
        descriptionEn: '',
        descriptionAr: '',
        price: '',
        currency: 'KWD',
        stock: 'in_stock',
        quantity: '1',
        length: '',
        width: '',
        weight: '',
        materials: '',
        method: 'handmade',
        colors: '',
        date: ''
    });
    const [uploadedImages, setUploadedImages] = useState([]);
    const [imagePreviews, setImagePreviews] = useState([]);
    const [manualImages, setManualImages] = useState('');

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleImageUpload = async (e) => {
        const files = Array.from(e.target.files);
        const newPreviews = [];
        const newUploadedImages = [];
        
        // معالجة كل صورة بشكل منفصل مع الضغط
        for (const file of files) {
            if (file.type.startsWith('image/')) {
                try {
                    // ضغط الصورة قبل الحفظ
                    const compressedBase64 = await compressImage(file, 1200, 1200, 0.7);
                    
                    newPreviews.push({
                        name: file.name,
                        url: compressedBase64,
                        data: compressedBase64
                    });
                    newUploadedImages.push(compressedBase64);
                } catch (error) {
                    console.error('Error processing image:', file.name, error);
                    alert(currentLang === 'ar' 
                        ? `خطأ في معالجة الصورة: ${file.name}` 
                        : `Error processing image: ${file.name}`);
                }
            }
        }
        
        if (newPreviews.length > 0) {
            setImagePreviews(prev => [...prev, ...newPreviews]);
            setUploadedImages(prev => [...prev, ...newUploadedImages]);
        }
    };

    const removeImage = (index) => {
        const newPreviews = imagePreviews.filter((_, i) => i !== index);
        const newUploaded = uploadedImages.filter((_, i) => i !== index);
        setImagePreviews(newPreviews);
        setUploadedImages(newUploaded);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        try {
            let images = [];
            if (uploadedImages.length > 0) {
                // استخدام الصور المرفوعة (base64)
                images = uploadedImages;
                
                // التحقق من حجم البيانات
                const totalSize = images.reduce((sum, img) => sum + (img.length * 3) / 4, 0);
                const sizeInMB = totalSize / (1024 * 1024);
                
                if (sizeInMB > 4) {
                    alert(currentLang === 'ar' 
                        ? 'الصور كبيرة جداً. يرجى رفع صور أصغر أو تقليل عدد الصور.' 
                        : 'Images are too large. Please upload smaller images or reduce the number of images.');
                    return;
                }
            } else if (manualImages.trim()) {
                // استخدام الصور اليدوية (أسماء ملفات من public folder)
                images = manualImages.split(',').map(img => img.trim()).filter(img => img);
            }

            if (images.length === 0) {
                alert(currentLang === 'ar' ? 'يرجى إضافة صورة واحدة على الأقل' : 'Please add at least one image');
                return;
            }

            const itemData = {
                id: editingItem ? editingItem.id : Date.now().toString(),
                ...formData,
                price: formData.price ? parseFloat(formData.price) : null,
                quantity: formData.quantity ? parseInt(formData.quantity) : null,
                length: formData.length ? parseFloat(formData.length) : null,
                width: formData.width ? parseFloat(formData.width) : null,
                weight: formData.weight ? parseFloat(formData.weight) : null,
                images: images,
                image: images[0],
                // تحديد نوع الصور: 'base64' للصور المرفوعة، 'file' للملفات من public
                imageType: uploadedImages.length > 0 ? 'base64' : 'file'
            };

            console.log('📝 AdminPanel: Submitting item data:', {
                id: itemData.id,
                price: itemData.price,
                titleEn: itemData.titleEn,
                isEdit: !!editingItem
            });

            if (editingItem) {
                onUpdate(itemData);
            } else {
                onAdd(itemData);
            }

            // Reset form
            setFormData({
                titleEn: '',
                titleAr: '',
                descriptionEn: '',
                descriptionAr: '',
                price: '',
                currency: 'KWD',
                stock: 'in_stock',
                quantity: '1',
                length: '',
                width: '',
                weight: '',
                materials: '',
                method: 'handmade',
                colors: '',
                date: ''
            });
            setUploadedImages([]);
            setImagePreviews([]);
            setManualImages('');
            setEditingItem(null);
        } catch (error) {
            if (error.name === 'QuotaExceededError') {
                alert(currentLang === 'ar' 
                    ? 'المساحة التخزينية ممتلئة. يرجى حذف بعض القطع أو استخدام صور أصغر.' 
                    : 'Storage quota exceeded. Please delete some items or use smaller images.');
            } else {
                console.error('Error in handleSubmit:', error);
                alert(currentLang === 'ar' 
                    ? 'حدث خطأ أثناء الحفظ: ' + (error.message || 'خطأ غير معروف') 
                    : 'Error saving: ' + (error.message || 'Unknown error'));
            }
        }
    };

    const handleEdit = (item) => {
        setEditingItem(item);
        setFormData({
            titleEn: item.titleEn || '',
            titleAr: item.titleAr || '',
            descriptionEn: item.descriptionEn || '',
            descriptionAr: item.descriptionAr || '',
            price: item.price || '',
            currency: item.currency || 'KWD',
            stock: item.stock || 'in_stock',
            quantity: item.quantity || '',
            length: item.length || '',
            width: item.width || '',
            weight: item.weight || '',
            materials: item.materials || '',
            method: item.method || 'handmade',
            colors: item.colors || '',
            date: item.date || ''
        });
        if (item.images && item.images.length > 0) {
            // التحقق من نوع الصور
            if (item.imageType === 'base64') {
                // الصور هي base64
                setManualImages('');
                setImagePreviews(item.images.map((img, idx) => ({
                    name: `image-${idx}.jpg`,
                    url: img,
                    data: img
                })));
                setUploadedImages(item.images);
            } else {
                // الصور هي أسماء ملفات
                setManualImages(item.images.join(','));
                setImagePreviews(item.images.map(img => ({
                    name: img,
                    url: `${process.env.PUBLIC_URL}/${img}`
                })));
                setUploadedImages([]);
            }
        } else {
            setManualImages('');
            setImagePreviews([]);
            setUploadedImages([]);
        }
        setIsExpanded(true);
    };

    const handleDelete = (id) => {
        if (window.confirm(currentLang === 'ar' ? 'هل أنت متأكد من حذف هذه القطعة؟' : 'Are you sure you want to delete this item?')) {
            onDelete(id);
        }
    };

    return (
        <div className={`admin-panel ${isExpanded ? 'expanded' : ''}`}>
            <div className="admin-panel-header" onClick={() => setIsExpanded(!isExpanded)}>
                <h3>{currentLang === 'ar' ? 'لوحة المدير' : 'Manager Panel'}</h3>
                <div className="admin-panel-controls">
                    <span className="expand-icon">{isExpanded ? '▼' : '▲'}</span>
                    <button className="logout-btn-small" onClick={onLogout}>
                        {currentLang === 'ar' ? 'تسجيل الخروج' : 'Logout'}
                    </button>
                </div>
            </div>

            {isExpanded && (
                <div className="admin-panel-content">
                    <div className="admin-actions">
                        <h4>{editingItem ? (currentLang === 'ar' ? 'تعديل القطعة' : 'Edit Item') : (currentLang === 'ar' ? 'إضافة قطعة فخار جديدة' : 'Add New Pottery Item')}</h4>
                        <form onSubmit={handleSubmit} className="admin-form">
                            <div className="form-row">
                                <div className="form-group">
                                    <label>{currentLang === 'ar' ? 'اسم القطعة (إنجليزي)' : 'Title (English)'}</label>
                                    <input
                                        type="text"
                                        name="titleEn"
                                        value={formData.titleEn}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>{currentLang === 'ar' ? 'اسم القطعة (عربي)' : 'Title (Arabic)'}</label>
                                    <input
                                        type="text"
                                        name="titleAr"
                                        value={formData.titleAr}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>{currentLang === 'ar' ? 'الوصف (إنجليزي)' : 'Description (English)'}</label>
                                    <textarea
                                        name="descriptionEn"
                                        value={formData.descriptionEn}
                                        onChange={handleInputChange}
                                        rows="3"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>{currentLang === 'ar' ? 'الوصف (عربي)' : 'Description (Arabic)'}</label>
                                    <textarea
                                        name="descriptionAr"
                                        value={formData.descriptionAr}
                                        onChange={handleInputChange}
                                        rows="3"
                                    />
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>{currentLang === 'ar' ? 'السعر' : 'Price'}</label>
                                    <input
                                        type="number"
                                        name="price"
                                        value={formData.price}
                                        onChange={handleInputChange}
                                        step="0.01"
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>{currentLang === 'ar' ? 'العملة' : 'Currency'}</label>
                                    <input
                                        type="text"
                                        name="currency"
                                        value={formData.currency}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>{currentLang === 'ar' ? 'حالة المخزون' : 'Stock Status'}</label>
                                    <select
                                        name="stock"
                                        value={formData.stock}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="in_stock">{currentLang === 'ar' ? 'متوفر' : 'In Stock'}</option>
                                        <option value="out_of_stock">{currentLang === 'ar' ? 'غير متوفر' : 'Out of Stock'}</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>{currentLang === 'ar' ? 'الكمية' : 'Quantity'}</label>
                                    <input
                                        type="number"
                                        name="quantity"
                                        value={formData.quantity}
                                        onChange={handleInputChange}
                                        min="0"
                                    />
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>{currentLang === 'ar' ? 'الطول (سم)' : 'Length (cm)'}</label>
                                    <input
                                        type="number"
                                        name="length"
                                        value={formData.length}
                                        onChange={handleInputChange}
                                        step="0.1"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>{currentLang === 'ar' ? 'العرض (سم)' : 'Width (cm)'}</label>
                                    <input
                                        type="number"
                                        name="width"
                                        value={formData.width}
                                        onChange={handleInputChange}
                                        step="0.1"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>{currentLang === 'ar' ? 'الوزن (جم)' : 'Weight (g)'}</label>
                                    <input
                                        type="number"
                                        name="weight"
                                        value={formData.weight}
                                        onChange={handleInputChange}
                                        step="0.1"
                                    />
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>{currentLang === 'ar' ? 'المواد المستخدمة' : 'Materials'}</label>
                                    <input
                                        type="text"
                                        name="materials"
                                        value={formData.materials}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>{currentLang === 'ar' ? 'طريقة التصنيع' : 'Method'}</label>
                                    <select
                                        name="method"
                                        value={formData.method}
                                        onChange={handleInputChange}
                                    >
                                        <option value="handmade">{currentLang === 'ar' ? 'يدوي' : 'Handmade'}</option>
                                        <option value="wheel">{currentLang === 'ar' ? 'عجلة خزاف' : 'Potter\'s Wheel'}</option>
                                        <option value="free_form">{currentLang === 'ar' ? 'تشكيل حر' : 'Free Form'}</option>
                                    </select>
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>{currentLang === 'ar' ? 'الألوان / التشطيبات' : 'Colors / Finishes'}</label>
                                    <input
                                        type="text"
                                        name="colors"
                                        value={formData.colors}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>{currentLang === 'ar' ? 'تاريخ الصنع' : 'Date Made'}</label>
                                    <input
                                        type="text"
                                        name="date"
                                        value={formData.date}
                                        onChange={handleInputChange}
                                    />
                                </div>
                            </div>
                            <div className="form-group">
                                <label>{currentLang === 'ar' ? 'رفع الصور' : 'Upload Images'}</label>
                                <input
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    onChange={handleImageUpload}
                                    className="file-input"
                                />
                                {imagePreviews.length > 0 && (
                                    <div className="image-preview-container">
                                        {imagePreviews.map((preview, index) => (
                                            <div key={index} className="image-preview-item">
                                                <img 
                                                    src={preview.url} 
                                                    alt={preview.name}
                                                    onError={(e) => {
                                                        console.error('Preview image failed to load:', preview.name);
                                                        e.target.style.display = 'none';
                                                    }}
                                                    style={{ display: 'block' }}
                                                />
                                                <button
                                                    type="button"
                                                    className="remove-image"
                                                    onClick={() => removeImage(index)}
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <small>
                                    {currentLang === 'ar' 
                                        ? 'أو أدخل أسماء الصور يدوياً (مفصولة بفاصلة)' 
                                        : 'Or enter image filenames manually (comma separated)'}
                                </small>
                            </div>
                            <div className="form-group">
                                <label>{currentLang === 'ar' ? 'أسماء الصور (اختياري)' : 'Image Filenames (optional)'}</label>
                                <input
                                    type="text"
                                    value={manualImages}
                                    onChange={(e) => setManualImages(e.target.value)}
                                    placeholder="pottery1.jpg,pottery2.jpg"
                                />
                            </div>
                            <button type="submit" className="submit-btn">
                                {editingItem 
                                    ? (currentLang === 'ar' ? 'تحديث' : 'Update') 
                                    : (currentLang === 'ar' ? 'إضافة' : 'Add')}
                            </button>
                            {editingItem && (
                                <button
                                    type="button"
                                    className="cancel-btn"
                                    onClick={() => {
                                        setEditingItem(null);
                                        setFormData({
                                            titleEn: '',
                                            titleAr: '',
                                            descriptionEn: '',
                                            descriptionAr: '',
                                            price: '',
                                            currency: 'KWD',
                                            stock: 'in_stock',
                                            quantity: '1',
                                            length: '',
                                            width: '',
                                            weight: '',
                                            materials: '',
                                            method: 'handmade',
                                            colors: '',
                                            date: ''
                                        });
                                        setUploadedImages([]);
                                        setImagePreviews([]);
                                        setManualImages('');
                                    }}
                                >
                                    {currentLang === 'ar' ? 'إلغاء' : 'Cancel'}
                                </button>
                            )}
                        </form>
                    </div>

                    <div className="admin-list">
                        <h4>{currentLang === 'ar' ? 'إدارة قطع الفخار' : 'Manage Pottery Items'}</h4>
                        <div className="pottery-admin-list">
                            {potteryItems.length === 0 ? (
                                <p>{currentLang === 'ar' ? 'لا توجد قطع فخار' : 'No pottery items'}</p>
                            ) : (
                                potteryItems.map((item) => (
                                    <div key={item.id} className="pottery-admin-item">
                                        <div className="pottery-admin-item-info">
                                            <div className="pottery-admin-item-title">
                                                {item.titleEn} / {item.titleAr}
                                            </div>
                                            <div className="pottery-admin-item-image">
                                                {item.images && item.images.length > 0 ? item.images.join(', ') : 'No images'}
                                            </div>
                                        </div>
                                        <div className="pottery-item-actions">
                                            <button
                                                className="edit-btn"
                                                onClick={() => handleEdit(item)}
                                            >
                                                {currentLang === 'ar' ? 'تعديل' : 'Edit'}
                                            </button>
                                            <button
                                                className="delete-btn"
                                                onClick={() => handleDelete(item.id)}
                                            >
                                                {currentLang === 'ar' ? 'حذف' : 'Delete'}
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminPanel;

